import { useRef, type KeyboardEvent } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { WireItem } from "../api/types.js";
import { ITEM_COLUMNS } from "./columns.js";

const ROW_HEIGHT = 56;

/**
 * The desktop/wide table. Virtualised with `@tanstack/react-virtual` since
 * the shop's full history is ~900 rows and growing, and rendering all of
 * them (times 16 columns) as real DOM nodes is the kind of thing that's
 * fine at first and then isn't.
 *
 * Built from ARIA `role="table"/"row"/"cell"` divs rather than a native
 * `<table>`: the virtualizer needs to absolutely-position rows inside a
 * sized container, which native table layout does not tolerate, but screen
 * readers still get table semantics.
 *
 * Row activation mirrors `listView_MouseDoubleClick` (MainWindow.xaml.cs:275)
 * exactly for a mouse: double-click opens the item. Pressing Enter on a
 * focused row does the same, since a double-click has no keyboard
 * equivalent otherwise and every interactive control must stay
 * keyboard-operable.
 */
export function ItemTable({ items, onActivate }: { items: WireItem[]; onActivate: (id: string) => void }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>, id: string) {
    if (event.key === "Enter") {
      event.preventDefault();
      onActivate(id);
    }
  }

  return (
    <div className="item-table-wrapper">
      <div className="item-table-header" role="row">
        {ITEM_COLUMNS.map((col) => (
          <div key={col.key} className="item-table-cell" style={{ width: col.width }} role="columnheader">
            {col.header}
          </div>
        ))}
      </div>
      <div className="item-table-scroll" ref={scrollRef} role="table" aria-label="Seznam vnosov v skladišču">
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = items[virtualRow.index];
            if (!item) return null;
            return (
              <div
                key={item.id}
                role="row"
                tabIndex={0}
                aria-label={`Vnos št. ${item.number ?? "?"}, ${item.lastname ?? ""} ${item.name ?? ""}`}
                className={`item-table-row${item.isFlagged ? " flagged" : ""}${virtualRow.index % 2 === 1 ? " striped" : ""}`}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                onDoubleClick={() => onActivate(item.id)}
                onKeyDown={(e) => handleKeyDown(e, item.id)}
              >
                {ITEM_COLUMNS.map((col) => (
                  <div
                    key={col.key}
                    className={`item-table-cell${col.multiline ? " multiline" : ""}`}
                    style={{ width: col.width }}
                    role="cell"
                  >
                    {col.render(item)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
